import { AuthConfig } from '@/client/types/AuthConfig';
import { AxiosResponse } from 'axios';
import { preAuthApiClient } from '@/client/services/api/clients/preAuthApiClient';

// Discriminated on `result` so a `!result` guard narrows `data` to non-null.
export type AuthCheckResult =
  | { result: true; data: AuthConfig }
  | { result: false; data: null; navigate?: string };

const toAuthCheckResult = (response: AxiosResponse<AuthConfig>): AuthCheckResult => {
  if (response?.data?.company?.suspended) {
    return { result: false, data: null, navigate: '/suspended' };
  }
  return { result: true, data: response.data };
};

/**
 * Validate a raw authtoken and hydrate the full AuthConfig — the shared step
 * of every token-handoff transition route (saml/oauth/hub).
 */
export const checkAuthToken = async (authToken: string): Promise<AuthCheckResult> => {
  try {
    const response = await preAuthApiClient.get<AuthConfig>('/v3/authtoken', {
      headers: {
        Authorization: `AuthToken token=${authToken}`,
      },
    });
    return toAuthCheckResult(response);
  } catch {
    return { result: false, data: null };
  }
};

/**
 * OAuth variant: exchanges the one-time sign-in key, and the identifier when the landing URL has
 * one, instead of a token. They go in the body: the api's access log records query strings, and
 * the identifier is usually the user's email.
 */
export const checkOauthCode = async (
  key: string,
  identifier?: string | null,
): Promise<AuthCheckResult> => {
  try {
    const response = await preAuthApiClient.post<AuthConfig>(
      '/v3/authtoken/oauth',
      identifier ? { key, identifier } : { key },
    );
    return toAuthCheckResult(response);
  } catch {
    return { result: false, data: null };
  }
};
